import React, { useEffect, useState } from 'react';
import { Spin, Tabs } from 'antd';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';
import { useDataProvider, usePermissions } from 'react-admin';
import { ExtendedDataProvider } from '@src/types';
import ServiceJson from './ServiceJson';
import ServiceExample from '@src/components/ServiceCatalogue/ServiceExample';
import { isVisibleCreateServiceButton } from '../utils';
import ServiceConfigsTab from './ServiceConfigsTab';
import ServiceSchemaTab from './ServiceSchemaTab';
import ServiceSettingsTab from './ServiceSettingsTab';
import { ServiceCatalogueConfig } from './types';
import '@src/assets/css/github_markdown.css';

/**
 * The service's own documentation, fetched the first time it is looked at.
 *
 * Off the resource the page is already reading: a service owner's own service
 * is not in the consumer browse list, so asking that endpoint for it 404s.
 */
const ReadmeTab = ({ record, resource }: { record: any; resource: string }) => {
    const dataProvider: ExtendedDataProvider = useDataProvider();
    const [markdown, setMarkdown] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        dataProvider
            .getActionNonJson(resource, { id: record.id, verb: 'docs' })
            .then((response: any) => {
                if (!cancelled) setMarkdown(String(response.data).replace(/\\n/gi, '  \n'));
            })
            .catch(() => {
                if (!cancelled) setMarkdown('');
            });
        return () => {
            cancelled = true;
        };
    }, [record.id, resource, dataProvider]);

    if (markdown === null) return <Spin style={{ width: '100%' }} />;
    if (!markdown) return <div className="svc-empty">No readme was published for this service.</div>;

    return (
        <ReactMarkdown className="markdown-body" rehypePlugins={[rehypeRaw, remarkGfm]}>
            {markdown}
        </ReactMarkdown>
    );
};

/**
 * What one service opens onto. Everyone gets what the service *is* — its
 * readme, its schema, an example of declaring one. Only the team that owns it
 * gets what administering it means, so those tabs are not rendered at all for
 * a consumer rather than rendered and disabled.
 */
const ServiceTabs = ({
    record,
    config,
    dependant,
}: {
    record: any;
    config: ServiceCatalogueConfig;
    /** a dependant service is someone else's — there is nothing to administer */
    dependant?: boolean;
}) => {
    const { permissions } = usePermissions();

    const owned = config.pov === 'serviceowner' && !dependant;

    /**
     * Settings, teams, Pack and the service config have always been the admin's
     * alone — everyone else on the owning team reads them. Kept as it was.
     */
    const canEdit = owned && permissions === 'ADMIN';

    /**
     * Authoring the schema is gated by the deployment's ServiceCreator setting
     * rather than by role, which is how it has always been — every member of
     * the owning team gets the editor where that setting is on. Read-only is
     * the exception: reading is the whole of what that role does.
     */
    const canAuthorSchema = owned && permissions !== 'READ_ONLY' && !!isVisibleCreateServiceButton();

    // a dependant service is listed from a different endpoint and carries only
    // its declaration, so that is all there is to show
    if (dependant) {
        return (
            <Tabs
                className="svc-tabs"
                items={[
                    {
                        key: 'schema',
                        label: 'Declaration',
                        children: (
                            <div className="svc-tab">
                                <ServiceJson data={record.schema ?? {}} />
                            </div>
                        ),
                    },
                ]}
            />
        );
    }

    const items = [
        ...(record.has_md
            ? [{
                  key: 'readme',
                  label: 'Readme',
                  children: <div className="svc-tab"><ReadmeTab record={record} resource={config.resource} /></div>,
              }]
            : []),
        {
            key: 'schema',
            label: 'Schema',
            children: <ServiceSchemaTab record={record} canEdit={canAuthorSchema} />,
        },
        {
            key: 'example',
            label: 'Example',
            children: <div className="svc-tab"><ServiceExample record={record} resource={config.resource} /></div>,
        },
        // Settings, the teams and Pack are all administration, and administering
        // is the owning team's — a consumer is not shown the tab at all.
        ...(owned
            ? [
                  {
                      key: 'settings',
                      label: 'Settings',
                      children: <div className="svc-tab"><ServiceSettingsTab record={record} canEdit={canEdit} /></div>,
                  },
                  {
                      key: 'configs',
                      label: 'Service configs',
                      children: <ServiceConfigsTab record={record} canEdit={canEdit} />,
                  },
              ]
            : []),
    ];

    return <Tabs className="svc-tabs" defaultActiveKey={items[0]?.key} items={items} />;
};

export default ServiceTabs;
